import { useForm } from "@inertiajs/react";
import IndustrySelector from "@/components/IndustrySelector";
import ListingStatusSelect from "@/components/ListingStatusSelect";
import LogoUpload from "@/components/LogoUpload";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { IndustrySelect } from "@/components/IndustrySelect";
import type { GicsTreeNode } from "@/lib/industry";
import type {
  BizminerAdminAccount,
  BizminerIndustry,
} from "@/types/serializers";

interface EditAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  account: BizminerAdminAccount;
  organizationId: number;
  gicsTree: GicsTreeNode[];
  industries: BizminerIndustry[];
}

export function EditAccountDialog({
  open,
  onOpenChange,
  account,
  organizationId,
  gicsTree,
  industries,
}: EditAccountDialogProps) {
  const { data, setData, patch, processing, reset, transform } = useForm({
    account: {
      name: account.name || "",
      logo: null as File | "" | null,
      industry_id: account.industry_id ?? null,
      gics_sub_industry_id: account.gics_sub_industry_id ?? null,
      listing_status: account.listing_status ?? "",
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    transform((formData) => {
      const submitData: Record<string, unknown> = {
        account: {
          name: formData.account.name,
          industry_id: formData.account.industry_id,
          gics_sub_industry_id: formData.account.gics_sub_industry_id,
          listing_status: formData.account.listing_status,
        },
      };

      if (formData.account.logo !== null) {
        (submitData.account as Record<string, unknown>).logo =
          formData.account.logo;
      }

      return submitData;
    });

    patch(`/admin/organizations/${organizationId}/accounts/${account.id}`, {
      forceFormData: !!data.account.logo,
      onSuccess: () => {
        onOpenChange(false);
        reset();
      },
    });
  };

  const handleClose = () => {
    onOpenChange(false);
    reset();
    setData("account.name", account.name || "");
    setData("account.industry_id", account.industry_id ?? null);
    setData("account.gics_sub_industry_id", account.gics_sub_industry_id ?? null);
    setData("account.listing_status", account.listing_status ?? "");
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[525px] text-foreground">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit Account</DialogTitle>
            <DialogDescription>
              Update the account name and logo.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="account-name">
                Account Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="account-name"
                type="text"
                value={data.account.name}
                onChange={(e) => setData("account.name", e.target.value)}
                placeholder="Enter account name"
                required
                autoFocus
              />
            </div>

            <div className="grid gap-2">
              <Label>Industry</Label>
              <IndustrySelect
                value={data.account.industry_id}
                onChange={(value) => setData("account.industry_id", value)}
                industries={industries}
              />
              <p className="text-xs text-muted-foreground">
                Determines which companies are offered as peers for comparison
              </p>
            </div>

            <div className="grid gap-2">
              <Label>GICS Classification</Label>
              <IndustrySelector
                value={data.account.gics_sub_industry_id}
                onChange={(value) =>
                  setData("account.gics_sub_industry_id", value)
                }
                treeData={gicsTree}
              />
              <p className="text-xs text-muted-foreground">
                Reference classification. Peer comparison uses the Industry
                field above.
              </p>
            </div>

            <ListingStatusSelect
              value={data.account.listing_status}
              onChange={(value) => setData("account.listing_status", value)}
            />

            <LogoUpload
              label="Logo"
              currentLogoUrl={account.logo_url}
              onLogoChange={(file) => setData("account.logo", file)}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                processing || !data.account.name || !data.account.listing_status
              }
            >
              {processing ? "Updating..." : "Update Account"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
